import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import type { IBlogArticlesNode as IBlogAllQueryNode } from "@repo/shopifyV2/interface/blog";
import type { Article, ArticleEdge } from "@repo/shopifyV2/types/storefront.types";

import { DEFAULT_TITLE } from "@const/meta";
import { URLS } from "@const/urls";

import useBlog from "@queries/useBlog";

import { useLayoutStore } from "@store/layout";

export default function useFeaturesDetail(handle: string, query: string) {
  const navigate = useNavigate();
  const location = useLocation();

  const { handle: articleHandle } = useParams();

  const { setMeta } = useLayoutStore();

  const [article, setArticle] = useState<IBlogAllQueryNode | null>(null);
  const [cursorList, setCursorList] = useState<(ArticleEdge & { node: IBlogAllQueryNode })[]>([]);
  const [nextArticle, setNextArticle] = useState<Article | null>(null);
  const [prevArticle, setPrevArticle] = useState<Article | null>(null);

  const { fetchBlog, blogLoading: loading } = useBlog();

  const fetchArticle = useCallback(
    () =>
      fetchBlog({ handle, articleHandle, cursor_first: 200, cursor_sortKey: "PUBLISHED_AT", cursor_query: query }).then(
        ({ data: _data }) => {
          if (!_data?.blog.articleByHandle) {
            navigate(URLS.HOME);
            return;
          }

          setArticle(_data?.blog?.articleByHandle);
          setCursorList(_data?.blog?.cursor_list?.edges ?? []);
        },
      ),
    [handle, articleHandle, query, navigate],
  );

  useEffect(() => {
    if (location.state?.article) {
      setArticle(location.state?.article as Article);
      setCursorList(location.state?.cursorList as ArticleEdge[]);
    } else {
      void fetchArticle();
    }

    cursorList?.forEach((cursor, index) => {
      if (cursor.node?.id === article?.id) {
        setNextArticle(index > 0 ? cursorList[index - 1]?.node : null);
        setPrevArticle(index < cursorList?.length ? cursorList[index + 1]?.node : null);
      }
    });
  }, [location, article, articleHandle, cursorList]);

  useEffect(() => {
    if (article?.title) {
      setMeta({ title: `${article?.title.replaceAll("<br>", "")} - ${DEFAULT_TITLE}` });
    }
  }, [article?.title]);

  const imageLength = useMemo(() => {
    const parser = new DOMParser();
    const content = parser.parseFromString(article?.contentHtml || "", "text/html");
    const getImageTags = content.body.getElementsByTagName("img");
    return getImageTags.length || 0;
  }, [article]);

  return { loading, article, prevArticle, nextArticle, imageLength };
}
